import * as React from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Menu, X, Bell, LogOut, Settings, ChevronDown, MoreHorizontal,
  Store, DollarSign, ShoppingBag, Wallet,
  Home, Compass, Bot, ListChecks, Trophy, Gift, Globe,
  Sun, Moon, Laptop,
} from 'lucide-react'
import { motion, AnimatePresence } from 'motion/react'

import { useAuthStore } from '@/store/auth'
import { useSettingsStore } from '@/store/settings'
import { SUPPORTED_LANGS, type LangCode } from '@/i18n'
import { cn } from '@/lib/utils'

// ── 頂部極簡 navbar ─────────────────────────────────────────────────────────
export function Navbar() {
  const { t } = useTranslation('common')
  const { user, clearAuth } = useAuthStore()
  const navigate = useNavigate()
  const [open, setOpen] = React.useState(false)

  // Esc 關抽屜
  React.useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  // 抽屜開啟時鎖背景捲動
  React.useEffect(() => {
    if (!open) return
    const orig = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = orig }
  }, [open])

  const onLogout = async () => { setOpen(false); await clearAuth(); navigate('/login') }

  return (
    <>
      <header
        className="sticky top-0 z-30 border-b-2 border-[var(--color-border)]"
        style={{ background: '#1a1a2e', height: 56 }}
      >
        {/* drawer 開啟時整列元素隱藏（invisible 保留高度避免內容跳動） */}
        <div className={cn('max-w-6xl mx-auto px-4 h-full flex items-center gap-3 transition-opacity', open && 'invisible opacity-0')}>
          <button
            onClick={() => setOpen(true)}
            className="flex items-center justify-center border-2 border-[var(--color-cream)] hover:bg-white/10 transition-colors"
            style={{ width: 36, height: 36, color: 'var(--color-cream)' }}
            aria-label={t('nav.menu')}
            title={t('nav.menu')}
          >
            <Menu className="w-4 h-4" />
          </button>
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <BrandLogo />
            <span className="font-pixel text-[var(--color-cream)] text-[11px] tracking-wide">PIXELTRAVEL</span>
          </Link>
          {/* 右側：avatar（身份識別）+ ⋯ 更多（語系 / 設定 / 登出） */}
          <div className="ml-auto flex items-center gap-2">
            {user && (
              <Link
                to="/profile"
                className="flex items-center gap-2 border-2 border-[var(--color-cream)] hover:bg-white/10 transition-colors"
                style={{ paddingLeft: 4, paddingRight: 10, paddingTop: 4, paddingBottom: 4 }}
                aria-label={t('nav.profile')}
                title={user.display_name ?? 'USER'}
              >
                <div
                  className="flex items-center justify-center border-2 border-[var(--color-navy)] font-bold"
                  style={{ width: 24, height: 24, background: 'var(--color-sun)', fontSize: 10, color: 'var(--color-foreground)' }}
                >
                  {(user.display_name ?? 'U').split(/\s+/).map((w: string) => w[0] ?? '').join('').slice(0, 2).toUpperCase() || 'U'}
                </div>
                <span className="font-pixel text-[var(--color-cream)] text-[8px] hidden lg:block truncate max-w-[80px]">
                  {(user.display_name ?? 'USER').toUpperCase().slice(0, 10)}
                </span>
              </Link>
            )}
            <MoreMenu user={user} onLogout={onLogout} />
          </div>
        </div>
      </header>

      <NavDrawer open={open} onClose={() => setOpen(false)} user={user} onLogout={onLogout} />
    </>
  )
}

// ── 品牌 logo（頂部 nav + 抽屜頭部共用） ──────────────────────────────────────
function BrandLogo() {
  return (
    <div
      className="flex items-center justify-center border-2 border-[var(--color-cream)]"
      style={{ background: 'var(--color-sky)', padding: '4px 8px' }}
    >
      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="#1a1a2e" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
        <path d="M2 12h20M12 4l4 8-4 8M6 8l2 4-2 4" />
      </svg>
    </div>
  )
}

// ── 抽屜 ─────────────────────────────────────────────────────────────────────
interface DrawerProps {
  open: boolean
  onClose: () => void
  user: ReturnType<typeof useAuthStore.getState>['user']
  onLogout: () => Promise<void>
}

function NavDrawer({ open, onClose, user, onLogout }: DrawerProps) {
  const { t } = useTranslation('common')
  const location = useLocation()

  const isActive = (to: string) =>
    location.pathname === to || (to !== '/dashboard' && location.pathname.startsWith(to))

  type Item = { to: string; label: string; Icon: React.ComponentType<{ className?: string }> }

  const MAIN: Item[] = [
    { to: '/dashboard',    label: t('nav.dashboard'), Icon: Home       },
    { to: '/explore',      label: t('nav.explore'),   Icon: Compass    },
    { to: '/ai/chat',      label: t('nav.ai'),        Icon: Bot        },
    { to: '/tasks',        label: t('nav.tasks'),     Icon: ListChecks },
    { to: '/achievements', label: t('nav.points'),    Icon: Trophy     },
    { to: '/rewards',      label: t('nav.rewards'),   Icon: Gift       },
  ]

  const MY: Item[] = user ? [
    { to: '/notifications', label: t('nav.notifications'), Icon: Bell        },
    { to: '/expenses',      label: t('nav.wallet'),        Icon: Wallet      },
    { to: '/my-purchases',  label: t('nav.purchases'),     Icon: ShoppingBag },
  ] : []

  const ROLES: Item[] = []
  if (user?.roles?.includes('store'))  ROLES.push({ to: '/my-stores',        label: t('nav.stores'), Icon: Store       })
  if (user?.roles?.includes('expert')) ROLES.push({ to: '/my-sales',         label: t('nav.sales'),  Icon: DollarSign  })
  // 管理後台移除：維運統一在 opscrew，aibotravel 不做後台

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* 遮罩 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-40 bg-black/50"
            onClick={onClose}
          />
          {/* 抽屜 */}
          <motion.aside
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed top-0 left-0 bottom-0 z-50 w-[280px] flex flex-col"
            style={{ background: '#1a1a2e' }}
          >
            {/* 抽屜頭部（與頂部 nav 對齊：☓ + Logo + PIXELTRAVEL） */}
            <div className="flex items-center gap-3 px-4 border-b-2 border-[var(--color-cream)]/20" style={{ height: 56 }}>
              <button
                onClick={onClose}
                className="flex items-center justify-center border-2 border-[var(--color-cream)] hover:bg-white/10 transition-colors"
                style={{ width: 36, height: 36, color: 'var(--color-cream)' }}
                aria-label={t('nav.close')}
                title={t('nav.close')}
              >
                <X className="w-4 h-4" />
              </button>
              <BrandLogo />
              <span className="font-pixel text-[var(--color-cream)] text-[11px] tracking-wide">PIXELTRAVEL</span>
            </div>
            {/* 抽屜內容（捲動） */}
            <nav className="flex-1 overflow-y-auto py-2">
              <DrawerSection label={t('nav.section_main')}>
                {MAIN.map((it) => (
                  <DrawerItem key={it.to} {...it} active={isActive(it.to)} onClose={onClose} />
                ))}
              </DrawerSection>

              {MY.length > 0 && (
                <DrawerSection label={t('nav.section_my')}>
                  {MY.map((it) => (
                    <DrawerItem key={it.to} {...it} active={isActive(it.to)} onClose={onClose} />
                  ))}
                </DrawerSection>
              )}

              {ROLES.length > 0 && (
                <DrawerSection label={t('nav.section_role')}>
                  {ROLES.map((it) => (
                    <DrawerItem key={it.to} {...it} active={isActive(it.to)} onClose={onClose} />
                  ))}
                </DrawerSection>
              )}

              {/* 設定入口集中於頂部 navbar「⋯ 更多」下拉，側邊欄不重複呈現 */}
            </nav>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}

// ── 子元件 ───────────────────────────────────────────────────────────────────
function DrawerSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-2">
      <div className="px-4 py-1 font-pixel text-[8px] text-[var(--color-cream)]/50 tracking-widest">▍ {label.toUpperCase()}</div>
      {children}
    </div>
  )
}

function DrawerItem({ to, label, Icon, active, onClose }: {
  to: string
  label: string
  Icon: React.ComponentType<{ className?: string }>
  active: boolean
  onClose: () => void
}) {
  return (
    <Link
      to={to}
      onClick={onClose}
      className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-white/10"
      style={{
        color: active ? 'var(--color-sun)' : 'var(--color-cream)',
        background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
      }}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span className="text-sm flex-1">{label}</span>
      {active && <span className="text-[var(--color-sun)] text-xs">▶</span>}
    </Link>
  )
}

// Navbar 右側「⋯ 更多」下拉：語系 + 設定 + 登出
function MoreMenu({ user, onLogout }: {
  user: ReturnType<typeof useAuthStore.getState>['user']
  onLogout: () => Promise<void>
}) {
  const { t } = useTranslation('common')
  const { lang, setLang, theme, setTheme } = useSettingsStore()
  const [open, setOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)
  const current = SUPPORTED_LANGS.find((l) => l.code === lang) ?? SUPPORTED_LANGS[0]

  const THEMES: { key: 'light' | 'dark' | 'system'; Icon: typeof Sun }[] = [
    { key: 'light',  Icon: Sun    },
    { key: 'dark',   Icon: Moon   },
    { key: 'system', Icon: Laptop },
  ]

  // 點外面 / Esc 關閉
  React.useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-center border-2 border-[var(--color-cream)] hover:bg-white/10 transition-colors"
        style={{ width: 36, height: 36, color: 'var(--color-cream)' }}
        aria-label={t('nav.more')}
        title={t('nav.more')}
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-50 min-w-[220px] border-2 border-[var(--color-border)] bg-[var(--color-surface)]"
          style={{ boxShadow: '4px 4px 0 0 var(--color-border)' }}
        >
          {/* 語系子區 */}
          <div className="px-3 py-2 border-b-2 border-[var(--color-border)]">
            <div className="flex items-center gap-1.5 font-pixel text-[8px] text-[var(--color-muted)] tracking-widest mb-1.5">
              <Globe className="w-3 h-3" />
              <span>LANGUAGE · {current.short}</span>
            </div>
            <div className="grid grid-cols-2 gap-1">
              {SUPPORTED_LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => { setLang(l.code); setOpen(false) }}
                  className={cn(
                    'flex items-center gap-1.5 px-2 py-1 text-[11px] text-left border transition-colors',
                    l.code === lang
                      ? 'border-[var(--color-sky)] bg-[var(--color-sky)]/15 text-[var(--color-foreground)] font-bold'
                      : 'border-transparent hover:bg-[var(--color-muted-bg)] text-[var(--color-foreground)]',
                  )}
                >
                  <span>{l.flag}</span>
                  <span className="flex-1 truncate">{l.short}</span>
                </button>
              ))}
            </div>
          </div>

          {/* 外觀 (theme) 子區 */}
          <div className="px-3 py-2 border-b-2 border-[var(--color-border)]">
            <div className="font-pixel text-[8px] text-[var(--color-muted)] tracking-widest mb-1.5">
              {t('nav.theme_label')} · {t(`nav.theme.${theme}`)}
            </div>
            <div className="grid grid-cols-3 gap-1">
              {THEMES.map(({ key, Icon }) => (
                <button
                  key={key}
                  onClick={() => setTheme(key)}
                  aria-pressed={theme === key}
                  className={cn(
                    'flex flex-col items-center gap-0.5 px-2 py-1.5 text-[10px] border transition-colors',
                    theme === key
                      ? 'border-[var(--color-sky)] bg-[var(--color-sky)]/15 text-[var(--color-foreground)] font-bold'
                      : 'border-transparent hover:bg-[var(--color-muted-bg)] text-[var(--color-foreground)]',
                  )}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{t(`nav.theme.${key}`)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* 設定連結 */}
          <Link
            to="/settings"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-[var(--color-muted-bg)] transition-colors text-[var(--color-foreground)]"
          >
            <Settings className="w-3.5 h-3.5" />
            <span className="flex-1">{t('nav.settings')}</span>
          </Link>

          {/* 登出 */}
          {user && (
            <button
              onClick={() => { setOpen(false); onLogout() }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-[var(--color-coral)]/10 transition-colors text-[var(--color-coral)] border-t-2 border-[var(--color-border)]"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="flex-1">{t('nav.logout')}</span>
            </button>
          )}
        </div>
      )}
    </div>
  )
}
